/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_CONFIGURATION, STEPS } from "../../../Common/store/data/commonData";
import { DEFAULT_MC12 } from "../data/mc12Data";

import {
  setCurrentStepImplementation,
  setSavedConfigurationsImplementation
} from "../../../Common/store/implementations/commonImplementation";

const initialState = {
  configuration: null,
  defaultConfiguration: {
    ...DEFAULT_CONFIGURATION,
    technology: "Mc12",
    configurationType: "MC12",
    ...DEFAULT_MC12
  },
  steps: STEPS.MC12,
  currentStep: STEPS.MC12.START,
  readOnlyMode: false,
  savedConfigurations: []
};

export const mc12 = createSlice({
  name: "mc12",
  initialState,
  reducers: {
    // LOGIC
    setMc12Configuration: (state, action) => {
      state.configuration = action.payload;
      // set currentStepId
      const stepsAsArray = Object.values(state.steps);
      // eslint-disable-next-line no-param-reassign
      state.currentStep = stepsAsArray.find((step) => step.id === action.payload.configData.currentStepId);
    },
    resetMc12: (state) => {
      return initialState;
    },
    setMc12CurrentStep: (state, action) => {
      setCurrentStepImplementation(state, action);
    },
    setMc12ConfigData: (state, action) => {
      const { configuration } = state;
      // eslint-disable-next-line no-param-reassign
      configuration.configData = action.payload;
    },
    setReadOnlyMode: (state, action) => {
      state.readOnlyMode = action.payload;
    },
    setMc12SavedConfigurations: (state, action) => {
      setSavedConfigurationsImplementation(state, action);
    },
    // TRACK
    setTrack: (state, action) => {
      state.configuration.configData.track = action.payload;
    },
    setTrackLength: (state, action) => {
      state.configuration.configData.track.length = action.payload;
    },
    setNbSegments: (state, action) => {
      state.configuration.configData.track.nbSegments = action.payload;
    },
    setNbStraightSegments: (state, action) => {
      state.configuration.configData.track.nbStraightSegments = action.payload;
    },
    setNbAngledSegments: (state, action) => {
      state.configuration.configData.track.nbAngledSegments = action.payload;
    },
    setOverallSizeX: (state, action) => {
      state.configuration.configData.track.overallSizeX = action.payload;
    },
    setOverallSizeY: (state, action) => {
      state.configuration.configData.track.overallSizeY = action.payload;
    },
    setIsTrackClosed: (state, action) => {
      state.configuration.configData.track.isTrackClosed = action.payload;
    },
    setCanAutoCloseTrack: (state, action) => {
      state.configuration.configData.track.canAutoCloseTrack = action.payload;
    },
    setShouldRecalculateGuideRails: (state, action) => {
      state.configuration.configData.track.shouldRecalculateGuideRails = action.payload;
    },
    setTrackPattern: (state, action) => {
      state.configuration.configData.track.trackPattern = action.payload;
    },
    setBackupTrack: (state, action) => {
      state.configuration.configData.backupTrack = action.payload;
    },
    setCalculatedGuideRails: (state, action) => {
      state.configuration.configData.track.calculatedGuideRails = action.payload;
    },
    setSegments: (state, action) => {
      state.configuration.configData.segments = action.payload;
    },
    setAccessories: (state, action) => {
      state.configuration.configData.accessories = action.payload;
    },
    // COMMUNICATION
    setCalculatedCommunication: (state, action) => {
      state.configuration.configData.track.calculatedCommunication = action.payload;
    },
    setShouldRecalculateCommunication: (state, action) => {
      state.configuration.configData.track.shouldRecalculateCommunication = action.payload;
    },
    // ACCESSORIES
    setSelectedAccessories: (state, action) => {
      state.configuration.configData.track.selectedAccessories = action.payload;
    },
    // ELECTRICAL INSTALLATION
    setCalculatedElectricalInstallation: (state, action) => {
      state.configuration.configData.track.calculatedElectricalInstallation = action.payload;
    },
    setShouldRecalculateElectricalInstallation: (state, action) => {
      state.configuration.configData.track.shouldRecalculateElectricalInstallation = action.payload;
    }
  }
});

export const {
  // LOGIC
  setMc12Configuration,
  resetMc12,
  setMc12CurrentStep,
  setMc12ConfigData,
  setMc12SavedConfigurations,
  // TRACK
  setTrack,
  setTrackLength,
  setNbSegments,
  setNbStraightSegments,
  setNbAngledSegments,
  setOverallSizeX,
  setOverallSizeY,
  setIsTrackClosed,
  setTrackPattern,
  setCanAutoCloseTrack,
  setBackupTrack,
  setSegments,
  setShouldRecalculateGuideRails,
  setGuideRails,
  setCalculatedGuideRails,
  // COMMUNICATION
  setCalculatedCommunication,
  setShouldRecalculateCommunication,
  // ACCESSORIES
  setSelectedAccessories,
  // ELECTRICAL INSTALLATION
  setCalculatedElectricalInstallation,
  setShouldRecalculateElectricalInstallation
} = mc12.actions;

// LOGIC
export const selectDefaultMc12Configuration = (state) => state.mc12.defaultConfiguration;
export const selectMc12Configuration = (state) => state.mc12.configuration;
export const selectMc12Steps = (state) => state.mc12.steps;
export const selectMc12CurrentStep = (state) => state.mc12.currentStep;
export const selectReadOnlyMode = (state) => state.mc12.readOnlyMode;
export const selectMc12SavedConfigurations = (state) => state.mc12.savedConfigurations;
// TRACK
export const selectTrack = (state) => state.mc12.configuration?.configData?.track;
export const selectTrackLength = (state) => state.mc12.configuration?.configData?.track?.length;
export const selectNbSegments = (state) => state.mc12.configuration?.configData?.track?.nbSegments;
export const selectNbStraightSegments = (state) => state.mc12.configuration?.configData?.track?.nbStraightSegments;
export const selectNbAngledSegments = (state) => state.mc12.configuration?.configData?.track?.nbAngledSegments;
export const selectSizeX = (state) => state.mc12.configuration?.track?.overallSizeX;
export const selectSizeY = (state) => state.mc12.configuration?.track?.overallSizeY;
export const selectIsTrackClosed = (state) => state.mc12.configuration?.configData?.track?.isTrackClosed;
export const selectCanAutoCloseTrack = (state) => state.mc12.configuration?.configData?.track?.canAutoCloseTrack;
export const selectTrackPattern = (state) => state.mc12.configuration?.configData?.track?.trackPattern;
export const selectBackupTrack = (state) => state.mc12.configuration?.configData?.backupTrack;
export const selectSegments = (state) => state.mc12.configuration?.configData?.segments;
export const selectShouldRecalculateGuideRails = (state) =>
  state.mc12.configuration?.configData?.track.shouldRecalculateGuideRails;
export const selectCalculatedGuideRails = (state) => state.mc12.configuration?.configData?.track?.calculatedGuideRails;
export const selectAccessories = (state) => state.mc12.configuration?.configData?.accessories;
// COMMUNICATION
export const selectCalculatedCommunication = (state) =>
  state.mc12.configuration?.configData?.track?.calculatedCommunication;
export const selectShouldRecalculateCommunication = (state) =>
  state.mc12.configuration?.configData?.track?.shouldRecalculateCommunication;
export const selectCalculatedCommunicationSfoCablesView = (state) =>
  state.mc12.configuration?.configData?.track?.calculatedCommunication?.sfoCablesView;
export const selectCalculatedCommunicationSercosCablesView = (state) =>
  state.mc12.configuration?.configData?.track?.calculatedCommunication?.sercosCablesView;
// ACCESSORIES
export const selectSelectedAccessories = (state) => state.mc12.configuration?.configData?.track?.selectedAccessories;
// ELECTRICAL INSTALLATION
export const selectPowerSupplies = (state) => state.mc12.configuration?.configData?.powerSupplies;
export const selectCalculatedElectricalInstallation = (state) =>
  state.mc12.configuration?.configData?.track?.calculatedElectricalInstallation;
export const selectShouldRecalculateElectricalInstallation = (state) =>
  state.mc12.configuration?.configData?.track?.shouldRecalculateElectricalInstallation;
export default mc12.reducer;
